import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/shared/hooks/use-toast';
import { useI18n } from '@/contexts/I18nContext';
import { SignInForm } from './SignInForm';
import { SignUpForm, type SignUpFormData } from './SignUpForm';
import { EmailVerificationStep } from './EmailVerificationStep';
import { AgeBlockedScreen } from './AgeBlockedScreen';
import { isAgeBlocked, meetsMinimumAge, setAgeBlocked } from '@/utils/ageGate';
import { classifySignUpError, logSignUpError } from '@/utils/authErrors';
import { pushEvent as gtmPushEvent } from '@/integrations/analytics/gtm';
import { GTM_SIGN_UP } from '@/integrations/analytics/events';

interface AuthFormProps {
  isSignUp: boolean;
  setIsSignUp?: (value: boolean) => void;
  onEmailSent?: (message: string) => void;
}

export const AuthForm = ({ isSignUp, setIsSignUp, onEmailSent }: AuthFormProps) => {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const { toast } = useToast();
  const { currentLanguage, t } = useI18n();

  const [loading, setLoading] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [signedUpData, setSignedUpData] = useState<{ email: string; username: string; isInstructor: boolean } | null>(null);
  const [blocked, setBlocked] = useState<boolean>(() => isAgeBlocked());

  // Re-check persisted block whenever the user enters the sign-up flow.
  useEffect(() => {
    if (isSignUp) setBlocked(isAgeBlocked());
  }, [isSignUp]);

  const handleBackToStart = () => {
    setIsSignUp?.(false);
  };

  const handleSignUpSubmit = async (data: SignUpFormData) => {
    // Age gate is now part of the sign-up form: block under-18 before creating
    // the account, persisting the block the same way the standalone step did.
    if (!meetsMinimumAge(data.dateOfBirth)) {
      setAgeBlocked();
      setBlocked(true);
      return;
    }

    setLoading(true);
    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const trimmedEmail = data.email.trim();
      const { error } = await signUp(trimmedEmail, data.password, {
        username: data.username,
        isInstructor: data.isInstructor,
        preferred_language: currentLanguage,
        timezone: userTimezone,
        beltRank: 'white',
        birthday: data.dateOfBirth,
      });

      if (error) {
        const { reason, messageKey } = classifySignUpError(error);
        logSignUpError(error, reason);

        const title =
          reason === 'weak_password'
            ? t('auth.error')
            : reason === 'email_already_registered'
              ? t('auth.email_already_registered')
              : t('auth.sign_up_error');

        toast({ title, description: t(messageKey), variant: 'destructive' });
        return;
      }

      // Account created — move to OTP verification. The `sign_up`
      // ad-conversion fires only after OTP is verified (see handleVerified),
      // so unverified accounts (typoed emails, abandoned inboxes) do not
      // inflate paid-ads conversion counts.
      setSignedUpData({ email: trimmedEmail, username: data.username, isInstructor: data.isInstructor });
      sessionStorage.setItem('auth_pending_email_verification', '1');
      setShowVerification(true);
    } catch {
      toast({ title: t('auth.error'), description: t('auth.unexpected_error'), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Called after verifyOtp succeeds — navigate away from PublicRoute before it redirects
  const handleVerified = () => {
    if (!signedUpData) return;
    gtmPushEvent(GTM_SIGN_UP);
    sessionStorage.removeItem('auth_pending_email_verification');
    navigate('/onboarding', {
      replace: true,
      state: { username: signedUpData.username, isInstructor: signedUpData.isInstructor },
    });
  };

  if (!isSignUp) {
    return <SignInForm onEmailSent={onEmailSent} onCreateAccount={() => setIsSignUp?.(true)} />;
  }

  if (blocked) {
    return <AgeBlockedScreen onBackToStart={handleBackToStart} />;
  }

  if (showVerification && signedUpData) {
    return (
      <EmailVerificationStep
        email={signedUpData.email}
        onVerified={handleVerified}
      />
    );
  }

  return (
    <SignUpForm
      onSubmit={handleSignUpSubmit}
      loading={loading}
    />
  );
};
